import { createTheme } from '@mui/material';
import {
    BORDER_RADIUS_NUMBER,
    COLOURS,
    COLOURS_DARK,
    COLOURS_EXTRA_DARK,
    COLOURS_EXTRA_LIGHT,
    COLOURS_LIGHT,
    FONT_FAMILY,
    FONT_SIZE_DEFAULT,
    FONT_SIZE_DEFAULT_NUMBER,
    FONT_SIZE_SMALL,
    FONT_WEIGHT_BOLD,
    FONT_WEIGHT_LIGHT,
    FONT_WEIGHT_REGULAR,
    LINE_HEIGHT_HEADING,
    LINE_HEIGHT_NARROW_CONTENT,
    LINE_HEIGHT_REGULAR,
    PAD_MEDIUM_NUMBER,
    TEXT_COLOURS_DEFAULT,
    TEXT_COLOURS_EXTRA_LIGHT,
    TEXT_COLOURS_LIGHT,
} from './designSystem';

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        primary: true;
    }
}

declare module '@mui/material/styles/createPalette' {
    interface Palette {
        neutral: PaletteColor;
    }

    interface PaletteOptions {
        neutral?: PaletteColorOptions;
    }
}

const theme = createTheme({
    palette: {
        action: {
            disabledBackground: COLOURS_EXTRA_LIGHT.grey,
            disabled: COLOURS_DARK.grey,
        },
        primary: {
            main: COLOURS.violet,
            light: COLOURS_LIGHT.violet,
            dark: COLOURS_DARK.violet,
            contrastText: COLOURS_EXTRA_DARK.violet,
        },
        secondary: {
            main: COLOURS.blue,
            light: COLOURS_LIGHT.blue,
            dark: COLOURS_DARK.blue,
            contrastText: COLOURS_EXTRA_DARK.blue,
        },
        error: {
            main: COLOURS.red,
            light: COLOURS_LIGHT.red,
            dark: COLOURS_DARK.red,
            contrastText: COLOURS_EXTRA_DARK.red,
        },
        text: {
            primary: TEXT_COLOURS_DEFAULT,
            secondary: TEXT_COLOURS_LIGHT,
            disabled: TEXT_COLOURS_EXTRA_LIGHT,
        },
        divider: COLOURS.violet,
        success: {
            main: COLOURS.green,
            light: COLOURS_LIGHT.green,
            dark: COLOURS_DARK.green,
            contrastText: COLOURS_EXTRA_DARK.green,
        },
        warning: {
            main: COLOURS.yellow,
            light: COLOURS_LIGHT.yellow,
            dark: COLOURS_DARK.yellow,
            contrastText: COLOURS_EXTRA_DARK.yellow,
        },
        info: {
            main: COLOURS.blue,
            light: COLOURS_LIGHT.blue,
            dark: COLOURS_DARK.blue,
            contrastText: COLOURS_EXTRA_DARK.blue,
        },
        neutral: {
            main: COLOURS.grey,
            light: COLOURS_LIGHT.grey,
            dark: COLOURS_DARK.grey,
            contrastText: COLOURS_EXTRA_DARK.grey,
        },
    },
    typography: {
        fontFamily: FONT_FAMILY,
        fontSize: FONT_SIZE_DEFAULT_NUMBER,
        fontWeightLight: FONT_WEIGHT_LIGHT,
        fontWeightRegular: FONT_WEIGHT_REGULAR,
        fontWeightMedium: FONT_WEIGHT_BOLD,
        fontWeightBold: FONT_WEIGHT_BOLD,
        htmlFontSize: FONT_SIZE_DEFAULT_NUMBER,
        h1: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_BOLD,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        h2: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_LIGHT,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        h3: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_LIGHT,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        h4: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_LIGHT,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        h5: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_LIGHT,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        h6: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_LIGHT,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        subtitle1: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_LIGHT,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        subtitle2: {
            fontSize: FONT_SIZE_SMALL,
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_LIGHT,
            lineHeight: LINE_HEIGHT_HEADING,
        },
        body1: {
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE_DEFAULT,
            fontWeight: FONT_WEIGHT_REGULAR,
            lineHeight: LINE_HEIGHT_REGULAR,
        },
        body2: {
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE_SMALL,
            fontWeight: FONT_WEIGHT_REGULAR,
            lineHeight: LINE_HEIGHT_REGULAR,
        },
        button: {
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_REGULAR,
            lineHeight: LINE_HEIGHT_HEADING,
            fontSize: FONT_SIZE_SMALL,
        },
        caption: {
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE_SMALL,
            fontWeight: FONT_WEIGHT_REGULAR,
            lineHeight: LINE_HEIGHT_NARROW_CONTENT,
        },
        overline: {
            fontFamily: FONT_FAMILY,
            fontWeight: FONT_WEIGHT_REGULAR,
            lineHeight: LINE_HEIGHT_NARROW_CONTENT,
            fontSize: FONT_SIZE_SMALL,
        },
    },
    spacing: PAD_MEDIUM_NUMBER,
    shape: {
        borderRadius: BORDER_RADIUS_NUMBER,
    },
    components: {
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: COLOURS.violet,
                },
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'primary' },
                    style: {
                        textTransform: 'lowercase',
                        border: `2px dashed ${COLOURS_DARK.blue}`,
                        color: COLOURS.blue,
                    },
                },
            ],
        },
    },
});

export default theme;
